import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CreditNoteService } from '../../services/creditNote.service';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { AuthService } from '../../services/auth.service';
import { SalesInvoice } from '../../interfaces/sales/salesInvoice.interface';
import { CreditableLine } from '../../interfaces/sales/creditNote.interface';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { formatMoney } from '../../utils/format';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments';
import { toLocalIsoDate } from '../../utils/dateUtils';

const round2 = (n: number) => Math.round(n * 100) / 100;

export const CreditNoteCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedInvoiceId = searchParams.get('invoiceId');

  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [invoiceId, setInvoiceId] = useState(lockedInvoiceId ?? '');
  const [lines, setLines] = useState<CreditableLine[]>([]);
  const [amounts, setAmounts] = useState<Record<string, number | ''>>({});
  const [noteDate, setNoteDate] = useState(toLocalIsoDate());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // B4-polish3: currency and rate of the chosen invoice (a credit note is booked at the original document rate)
  const [invoiceFx, setInvoiceFx] = useState<{ currencyCode: string; exchangeRate: number } | null>(null);

  useEffect(() => {
    if (lockedInvoiceId) return;
    const institutionId = AuthService.getSelectedInstitution()!!;
    SalesInvoiceService.listByInstitution(institutionId, 0, 500)
      .then(r => setInvoices(r.content.filter(i => i.status !== 'DRAFT' && i.status !== 'VOIDED')))
      .catch(e => toast.showApiError(e, 'Failed to load invoices'));
  }, [lockedInvoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setInvoiceFx(null);
    if (!invoiceId) return;
    let cancelled = false;
    Promise.resolve(SalesInvoiceService.getById(invoiceId))
      .then(inv => {
        if (!cancelled && inv && isForeignCurrency(inv.currencyCode) && inv.exchangeRate) {
          setInvoiceFx({ currencyCode: inv.currencyCode!, exchangeRate: inv.exchangeRate });
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load the invoice currency'));
    return () => { cancelled = true; };
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fcHint = (bdt: number) => invoiceFx
    ? `= ${formatFc(invoiceFx.currencyCode, round2(bdt / invoiceFx.exchangeRate))} at ${invoiceFx.exchangeRate}`
    : '';

  useEffect(() => {
    setAmounts({});
    if (!invoiceId) { setLines([]); return; }
    CreditNoteService.creditableLines(invoiceId)
      .then(setLines)
      .catch(e => toast.showApiError(e, 'Failed to load invoice lines'));
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const picked = useMemo(
    () => lines.filter(l => amounts[l.salesInvoiceLineId] !== undefined && Number(amounts[l.salesInvoiceLineId]) > 0),
    [lines, amounts]);
  const overLine = picked.some(l => Number(amounts[l.salesInvoiceLineId]) > l.remainingNet + 0.005);
  const netTotal = round2(picked.reduce((s, l) => s + Number(amounts[l.salesInvoiceLineId]), 0));
  const invalid = !invoiceId || picked.length === 0 || overLine || !reason.trim() || !noteDate;

  const handleSave = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      const created = await CreditNoteService.create({
        salesInvoiceId: invoiceId,
        noteDate,
        reason: reason.trim(),
        lines: picked.map(l => ({ salesInvoiceLineId: l.salesInvoiceLineId, amount: Number(amounts[l.salesInvoiceLineId]) })),
      });
      toast.showSuccess(`Credit note ${created.creditNoteNumber} posted`);
      navigate(`/sales/credit-notes/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.creditNoteCreate.page">
      <Typography variant="h5" sx={{ mb: 2 }}>New Credit Note</Typography>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {!lockedInvoiceId && (
              <Box sx={{ flex: '1 1 320px', maxWidth: 520 }}>
                <SearchableSelect<SalesInvoice>
                  data-help="sales.creditNoteCreate.invoice"
                  label="Invoice"
                  required
                  options={invoices}
                  value={invoiceId}
                  onChange={setInvoiceId}
                  getOptionLabel={i => `${i.invoiceNumber ?? 'Draft'} - ${i.customerName ?? ''} - ${formatMoney(i.totalAmount)}`}
                  noOptionsText="No issued invoices"
                />
              </Box>
            )}
            <Box sx={{ flex: '1 1 180px', maxWidth: 240 }}>
              <TextField data-help="sales.creditNoteCreate.noteDate" label="Note date" type="date" required fullWidth value={noteDate}
                onChange={e => setNoteDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
          </Box>
          <TextField data-help="sales.creditNoteCreate.reason" label="Reason" required multiline minRows={2} fullWidth value={reason}
            onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 1000 }} />
          {invoiceFx && (
            <Alert severity="info">
              This invoice is in {invoiceFx.currencyCode} at rate {invoiceFx.exchangeRate}. Enter the credit in BDT; it is
              booked at the original invoice rate, so the foreign amount is the BDT amount divided by that rate.
            </Alert>
          )}
          <Alert severity="info">
            Enter the net amount to credit per line (up to what is still creditable). Tax is reversed in proportion,
            on the note date. The credit lowers the invoice balance; any excess stays as an unapplied credit you can
            apply to another invoice or refund.
          </Alert>
          <ScrollableTable>
            <Table size="small" data-help-section="sales.creditNoteCreate.lines">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.creditNoteCreate.col.line">Line</TableCell>
                  <TableCell align="right" data-help="sales.creditNoteCreate.col.lineTotal">Line total</TableCell>
                  <TableCell align="right" data-help="sales.creditNoteCreate.col.credited">Already credited</TableCell>
                  <TableCell align="right" data-help="sales.creditNoteCreate.col.remaining">Still creditable</TableCell>
                  <TableCell align="right" data-help="sales.creditNoteCreate.col.credit">Credit (net)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map(l => {
                  const v = amounts[l.salesInvoiceLineId] ?? '';
                  const over = v !== '' && Number(v) > l.remainingNet + 0.005;
                  return (
                    <TableRow key={l.salesInvoiceLineId}>
                      <TableCell>
                        {l.description ?? '—'}
                        {!l.creditable && (
                          <Typography variant="caption" color="error" display="block">{l.notCreditableReason}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatMoney(l.lineTotal)}</TableCell>
                      <TableCell align="right">{formatMoney(l.creditedNet)}</TableCell>
                      <TableCell align="right">{formatMoney(l.remainingNet)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                          <TextField data-help="sales.creditNoteCreate.col.credit" size="small" type="number" disabled={!l.creditable || l.remainingNet <= 0}
                            value={v} error={over}
                            inputProps={{ min: 0, step: '0.01', 'aria-label': `Credit amount for ${l.description ?? 'line'}` }}
                            onChange={e => setAmounts(a => ({ ...a, [l.salesInvoiceLineId]: e.target.value === '' ? '' : Number(e.target.value) }))}
                            sx={{ flex: '1 1 110px', minWidth: 100, maxWidth: 200 }} />
                          <Button size="small" data-help="sales.creditNoteCreate.full" disabled={!l.creditable || l.remainingNet <= 0}
                            onClick={() => setAmounts(a => ({ ...a, [l.salesInvoiceLineId]: l.remainingNet }))}>
                            Full
                          </Button>
                          {invoiceFx && v !== '' && Number(v) > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%', textAlign: 'right' }}>
                              {fcHint(Number(v))}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTable>
          <Typography>Net credit: <b>{formatMoney(netTotal)}</b> (tax added on posting){invoiceFx && netTotal > 0 && <> {fcHint(netTotal)}</>}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" data-help="sales.creditNoteCreate.post" onClick={handleSave} disabled={saving || invalid}>
              {saving ? 'Posting…' : 'Post credit note'}
            </Button>
            <Button variant="outlined" data-help="sales.creditNoteCreate.cancel" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CreditNoteCreate;
